import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import { AdminApprovals, AdminUsers, ReviewTimesheet } from "@/pages/AdminPages";
import { AccountsList, AccountsExports, AccountsView } from "@/pages/AccountsPages";
import { Loader2 } from "lucide-react";

function Loading() {
  return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>;
}

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function Root() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin" || user.role === "super_admin") return <Navigate to="/admin" replace />;
  if (user.role === "accounts") return <Navigate to="/accounts" replace />;
  return <Navigate to="/timesheet" replace />;
}

function AppRouter() {
  const location = useLocation();
  // Handle auth callbacks synchronously (URL fragment) before any other route resolves
  const hash = location.hash || (typeof window !== "undefined" ? window.location.hash : "");
  if (hash?.includes("firebase_token=") || hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Root />} />
      <Route path="/timesheet" element={<Protected roles={["employee","admin","super_admin"]}><EmployeeDashboard /></Protected>} />
      <Route path="/admin" element={<Protected roles={["admin","super_admin"]}><AdminApprovals /></Protected>} />
      <Route path="/admin/users" element={<Protected roles={["super_admin"]}><AdminUsers /></Protected>} />
      <Route path="/admin/review/:userId/:year/:month" element={<Protected roles={["admin","super_admin"]}><ReviewTimesheet /></Protected>} />
      <Route path="/accounts" element={<Protected roles={["accounts"]}><AccountsList /></Protected>} />
      <Route path="/accounts/exports" element={<Protected roles={["accounts"]}><AccountsExports /></Protected>} />
      <Route path="/accounts/view/:userId/:year/:month" element={<Protected roles={["accounts","admin","super_admin"]}><AccountsView /></Protected>} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
